import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  Card,
  ButtonGroup,
  Form,
  Frame,
  Layout,
  Page,
  Toast,
  Stack,
  FormLayout,
  TextField,
  Checkbox,
  Modal,
  TextContainer,
  Heading,
  Link,
  Badge,
  RangeSlider,
  Select,
} from '@shopify/polaris';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faTwitter, faInstagramSquare, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultGrayColor, defaultDarkGrayColor, hslaColor, hexColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

const PostApplyPage = ({ history, shopifyToken, shopifyDomain, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;

  const isSubscribedUser = !isEmpty(currentSubscription);
  const canUpdateContent =
    isSubscribedUser && (currentSubscription.subscriptionType === 'Premium' || currentSubscription.subscriptionType === 'Growth');

  const [form, setForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const togglePreviewModal = () => setPreview(!preview);

  const [formErrors, setFormErrors] = useState({
    postApplyHeading: false,
    postApplyBody: false,
    postApplyButton: false,
  });

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const postApplyPagePage = propOr({}, 'post_apply_page')(styleControls);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const postApplyHeading = propOr('', 'postApplyHeading')(careerPortal);
    const postApplyBody = propOr('', 'postApplyBody')(careerPortal);
    const postApplyButton = propOr('', 'postApplyButton')(careerPortal);
    const postApplyButtonEnabled = propOr(true, 'postApplyButtonEnabled')(careerPortal);

    const instagramUrl = propOr('', 'instagramUrl')(careerPortal);
    const instagramButtonEnabled = propOr(false, 'instagramButtonEnabled')(careerPortal);
    const facebookUrl = propOr('', 'facebookUrl')(careerPortal);
    const facebookButtonEnabled = propOr(false, 'facebookButtonEnabled')(careerPortal);
    const twitterUrl = propOr('', 'twitterUrl')(careerPortal);
    const twitterButtonEnabled = propOr(false, 'twitterButtonEnabled')(careerPortal);
    const linkedinUrl = propOr('', 'linkedinUrl')(careerPortal);
    const linkedinButtonEnabled = propOr(false, 'linkedinButtonEnabled')(careerPortal);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);

    setForm((prevForm) => ({
      ...prevForm,
      postApplyHeading,
      postApplyBody,
      postApplyButton,
      postApplyButtonEnabled,
      instagramUrl,
      instagramButtonEnabled,
      facebookUrl,
      facebookButtonEnabled,
      twitterUrl,
      twitterButtonEnabled,
      linkedinUrl,
      linkedinButtonEnabled,
      ...styleControlsFormatted,
      postApplyPage: {
        ...prevForm.postApplyPage,
        enabled: true,
        backgroundColor: postApplyPagePage?.background_color || defaultWhiteColor,
        headingColor: postApplyPagePage?.heading_color || defaultGrayColor,
        headingWeight: postApplyPagePage?.heading_weight || '700',
        headingSize: postApplyPagePage?.heading_size || '26',
        headingFontFamily: postApplyPagePage?.heading_font_family || 'inherit',
        bodyColor: postApplyPagePage?.body_color || defaultGrayColor,
        bodyWeight: postApplyPagePage?.body_weight || '400',
        bodySize: postApplyPagePage?.body_size || '20',
        bodyFontFamily: postApplyPagePage?.body_font_family || 'inherit',
        buttonColor: postApplyPagePage?.button_color || defaultDarkGrayColor,
        buttonTextColor: postApplyPagePage?.button_text_color || defaultWhiteColor,
        socialButtonColor: postApplyPagePage?.social_button_color || defaultDarkGrayColor,
        buttonTextWeight: postApplyPagePage?.button_text_weight || '300',
        buttonTextCasing: postApplyPagePage?.button_text_casing || 'uppercase',
        buttonFontFamily: postApplyPagePage?.button_font_family || 'inherit',
      },
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const updateField = (fieldName) => (value) => {
    let errors = { ...formErrors, [fieldName]: false };
    setFormErrors(errors);
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      postApplyPage: {
        ...form.postApplyPage,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setIsSaveBarActive(false);
    setShowSuccessBanner(true);
    setIsLoading(false);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit = (e) => {
    e?.preventDefault();
    setPreview(false);
    setIsLoading(true);
    const careerPortal = propOr({}, 'careerPortal')(data);
    const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
    const variables = {
      ...careerPortal,
      ...form,
      shopifyToken,
    };
    delete variables.styleControls;

    save({ variables }).then(onSuccess).catch(onError);
  };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Post apply page'
          titleMetadata={<Badge status={'success'}>Published</Badge>}
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: false,
            nextURL: null,
            previousURL: !isSaveBarActive ? `/career-portal/job-description-page` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button primary onClick={onSubmit} disabled={!isSaveBarActive}>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Post apply page updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={careerPageLink}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Tell applicants what to expect next in the apply process and where to find more information about your
                      brand.
                    </p>
                    {!canUpdateContent && (
                      <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                        Upgrade account plan
                      </Link>
                    )}
                  </>
                }>
                <Card sectioned>
                  <Card.Section>
                    <FormLayout>
                      <div>
                        <TextField
                          id='postApplyHeading'
                          label='Title text'
                          value={form.postApplyHeading}
                          name='postApplyHeading'
                          onChange={updateField('postApplyHeading')}
                          placeholder='Success!'
                          disabled={!canUpdateContent}
                        />
                        {formErrors.postApplyHeading && (
                          <InlineError message='Title text is required' fieldID='postApplyHeading' />
                        )}
                      </div>
                      <div>
                        <TextField
                          id='postApplyBody'
                          label='Message text'
                          value={form.postApplyBody}
                          name='postApplyBody'
                          onChange={updateField('postApplyBody')}
                          placeholder='Thank you for applying'
                          disabled={!canUpdateContent}
                        />
                        {formErrors.postApplyBody && <InlineError message='Message text is required' fieldID='postApplyBody' />}
                      </div>
                      <div>
                        <TextField
                          id='buttonText'
                          label='Button text'
                          value={form.postApplyButton}
                          name='postApplyButton'
                          onChange={updateField('postApplyButton')}
                          placeholder='Back to all jobs'
                          disabled={!canUpdateContent}
                        />
                        {formErrors.buttonText && <InlineError message='Button text is required' fieldID='postApplyButton' />}
                      </div>
                      <Checkbox
                        name='postApplyButtonEnabled'
                        id='postApplyButtonEnabled'
                        label='Display button'
                        ariaControls='basic-collapsible'
                        checked={form.postApplyButtonEnabled}
                        onChange={updateField('postApplyButtonEnabled')}
                        disabled={!canUpdateContent}
                      />
                    </FormLayout>
                  </Card.Section>
                  <Card.Section title={'Social media'}>
                    <br />
                    <FormLayout>
                      <TextField
                        id='instagramUrl'
                        label='Instagram'
                        value={form.instagramUrl}
                        name='instagramUrl'
                        onChange={updateField('instagramUrl')}
                        placeholder='https://instagram.com/shophire'
                        disabled={!canUpdateContent}
                      />
                      <Checkbox
                        name='instagramButtonEnabled'
                        id='instagramButtonEnabled'
                        label='Display button'
                        ariaControls='basic-collapsible'
                        checked={form.instagramButtonEnabled}
                        onChange={updateField('instagramButtonEnabled')}
                        disabled={!canUpdateContent}
                      />
                      <TextField
                        id='twitterUrl'
                        label='Twitter'
                        value={form.twitterUrl}
                        name='twitterUrl'
                        onChange={updateField('twitterUrl')}
                        placeholder='https://twitter.com/shophire'
                        disabled={!canUpdateContent}
                      />
                      <Checkbox
                        name='twitterButtonEnabled'
                        id='twitterButtonEnabled'
                        label='Display button'
                        ariaControls='basic-collapsible'
                        checked={form.twitterButtonEnabled}
                        onChange={updateField('twitterButtonEnabled')}
                        disabled={!canUpdateContent}
                      />
                      <TextField
                        id='facebookUrl'
                        label='Facebook'
                        value={form.facebookUrl}
                        name='facebookUrl'
                        onChange={updateField('facebookUrl')}
                        placeholder='https://facebook.com/shophire'
                        disabled={!canUpdateContent}
                      />
                      <Checkbox
                        name='facebookButtonEnabled'
                        id='facebookButtonEnabled'
                        label='Display button'
                        ariaControls='basic-collapsible'
                        checked={form.facebookButtonEnabled}
                        onChange={updateField('facebookButtonEnabled')}
                        disabled={!canUpdateContent}
                      />
                      <TextField
                        id='linkedinUrl'
                        label='Linkedin'
                        value={form.linkedinUrl}
                        name='linkedinUrl'
                        onChange={updateField('linkedinUrl')}
                        placeholder='https://linkedin.com/shophire'
                        disabled={!canUpdateContent}
                      />
                      <Checkbox
                        name='linkedinButtonEnabled'
                        id='linkedinButtonEnabled'
                        label='Display button'
                        ariaControls='basic-collapsible'
                        checked={form.linkedinButtonEnabled}
                        onChange={updateField('linkedinButtonEnabled')}
                        disabled={!canUpdateContent}
                      />
                    </FormLayout>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and page background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.postApplyPage?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title text color'
                      defaultColor={form.postApplyPage?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.postApplyPage?.headingSize}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.postApplyPage?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'headingFontFamily' })}
                        value={form.postApplyPage?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'headingWeight' })}
                        value={form.postApplyPage?.headingWeight}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='MESSAGE TEXT'>
                    <ColorPicker
                      colorName='Job text color'
                      defaultColor={form.postApplyPage?.bodyColor}
                      onSelectColor={updateColor('bodyColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Message font size'
                        value={form.postApplyPage?.bodySize}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'bodySize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.postApplyPage?.bodySize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Message font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'bodyFontFamily' })}
                        value={form.postApplyPage?.bodyFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Message font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'bodyWeight' })}
                        value={form.postApplyPage?.bodyWeight}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='BUTTON'>
                    <ColorPicker
                      colorName='Button color'
                      defaultColor={form.postApplyPage?.buttonColor}
                      onSelectColor={updateColor('buttonColor')}
                    />
                    <ColorPicker
                      colorName='Button text color'
                      defaultColor={form.postApplyPage?.buttonTextColor}
                      onSelectColor={updateColor('buttonTextColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'buttonFontFamily' })}
                        value={form.postApplyPage?.buttonFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'buttonTextWeight' })}
                        value={form.postApplyPage?.buttonTextWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'postApplyPage', fieldName: 'buttonTextCasing' })}
                        value={form.postApplyPage?.buttonTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='Social media icons'>
                    <ColorPicker
                      colorName='Icon color'
                      defaultColor={form.postApplyPage?.socialButtonColor}
                      onSelectColor={updateColor('socialButtonColor')}
                    />
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive}>
                Publish
              </Button>
            </div>
          </Form>
          <Modal
            open={!!preview}
            large
            onClose={togglePreviewModal}
            primaryAction={{
              content: 'Publish',
              disabled: !isSaveBarActive,
              onAction: () => {
                onSubmit();
              },
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: togglePreviewModal,
              },
            ]}
            title={
              <TextContainer>
                <Heading>Preview post apply page</Heading>
                <p style={{ fontSize: '0.85em' }}>
                  Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
                  section will look on your store
                </p>
              </TextContainer>
            }
            loading={false}>
            <div className={'preview__modal'}>
              <div
                className='post_apply_wrapper'
                style={{
                  backgroundColor: `${hslaColor(form.postApplyPage?.backgroundColor, '#fff')}`,
                }}>
                <div className='shophire_card'>
                  <h1
                    className='text_heading'
                    style={{
                      color: `${hslaColor(form.postApplyPage?.headingColor, '#3a3a3a')}`,
                      fontSize: `${form.postApplyPage?.headingSize || '26'}px`,
                      fontWeight: `${form.postApplyPage?.headingWeight || '700'}`,
                    }}>
                    {form.postApplyHeading || 'Success!'}
                  </h1>
                  <h2
                    className='text_heading'
                    style={{
                      color: `${hslaColor(form.postApplyPage?.bodyColor, '#3a3a3a')}`,
                      fontSize: `${form.postApplyPage?.bodySize || '20'}px`,
                      fontWeight: `${form.postApplyPage?.bodyWeight || '400'}`,
                    }}>
                    {form.postApplyBody || 'Thanks for applying'}
                  </h2>
                  {form.postApplyButtonEnabled !== false && (
                    <a
                      className={'shophire_button'}
                      style={{
                        color: `${hslaColor(form.postApplyPage?.buttonTextColor, '#3a3a3a')}`,
                        backgroundColor: `${hslaColor(form.postApplyPage?.buttonColor, '#3a3a3a')}`,
                      }}>
                      {form.postApplyButton || 'Back to all jobs'}
                    </a>
                  )}
                  {(!!form.instagramButtonEnabled ||
                    !!form.facebookButtonEnabled ||
                    !!form.twitterButtonEnabled ||
                    !!form.linkedinButtonEnabled) && (
                    <>
                      <br />
                      <br />
                      <Stack>
                        {!!form.twitterButtonEnabled && (
                          <a href={form.twitterUrl} target={'_blank'}>
                            <FontAwesomeIcon
                              icon={faTwitter}
                              size={'2x'}
                              color={hexColor(form.postApplyPage.socialButtonColor, '#3a3a3a')}
                            />
                          </a>
                        )}
                        {!!form.facebookButtonEnabled && (
                          <a href={form.facebookUrl} target={'_blank'} className={'social_icon'}>
                            <FontAwesomeIcon
                              icon={faFacebook}
                              size={'2x'}
                              color={hexColor(form.postApplyPage.socialButtonColor, '#3a3a3a')}
                            />
                          </a>
                        )}
                        {!!form.instagramButtonEnabled && (
                          <a href={form.instagramUrl} target={'_blank'}>
                            <FontAwesomeIcon
                              icon={faInstagramSquare}
                              size={'2x'}
                              color={hexColor(form.postApplyPage.socialButtonColor, '#3a3a3a')}
                            />
                          </a>
                        )}
                        {!!form.linkedinButtonEnabled && (
                          <a href={form.linkedinUrl} target={'_blank'}>
                            <FontAwesomeIcon
                              icon={faLinkedin}
                              size={'2x'}
                              color={hexColor(form.postApplyPage.socialButtonColor, '#3a3a3a')}
                            />
                          </a>
                        )}
                      </Stack>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Modal>
        </Page>
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default PostApplyPage;
